import { Quill, RangeStatic } from "quill";
import Delta from "quill-delta";
import { LoggerFactory } from "../../../../utilities/logger";

export class RichTextBoxIeQuillBehaviorCorrector {
  static readonly mutationObserverInit: MutationObserverInit = { attributes: true, childList: true, subtree: true, characterData: true };

  constructor(private quill: Quill, private onError: () => void, private onTextChange: () => void) {
    this.quill.root.addEventListener("compositionstart", this.quillRootCompositionstartListener);
    this.quill.root.addEventListener("compositionupdate", this.quillRootCompositionupdateListener);
    this.quill.root.addEventListener("compositionend", this.quillRootCompositionendListener);
    this.quill.root.addEventListener("keydown", this.quillRootKeydownListener);
    this.quill.root.addEventListener("keyup", this.quillRootKeyupListener);
    this.quill.root.addEventListener("focusout", this.quillRootFocusoutListener);
    document.addEventListener("selectionchange", this.onSelectionChangeListener);
    this.quillMutationObserver();
    // IEでIMEを使用した入力が出来なくなる段落が存在したための対応
    // 発生しないオブジェクトにとっては無意味だが、判断ができない。また、起動直後に呼び出すとうまく動作しないため初回選択時（フォーカス時）に発生すようにした
    this.quill.once("selection-change", this.quillRefresh);
  }

  private readonly _logger = LoggerFactory.getLogger();
  private observer: MutationObserver = null;
  private observerTimer: number = null;
  private compositing: boolean = false;
  private compositionupdated: boolean = null;
  private compositionCanceling: boolean = false;
  private compositingContents: Delta = null;
  private compositingRange: RangeStatic = null;
  private lastContents: Delta = null;

  private readonly quillRootCompositionstartListener = (e: CompositionEvent) => this.quillRootCompositionstart(e);
  private readonly quillRootCompositionupdateListener = (e: CompositionEvent) => this.quillRootCompositionupdate(e);
  private readonly quillRootCompositionendListener = (e: CompositionEvent) => this.quillRootCompositionend(e);
  private readonly quillRootKeydownListener = (e: KeyboardEvent) => this.quillRootKeydown(e);
  private readonly quillRootKeyupListener = (e: KeyboardEvent) => this.quillRootKeyup(e);
  private readonly quillRootFocusoutListener = (e: FocusEvent) => this.quillRootFocusout(e);
  private readonly onSelectionChangeListener = () => this.onSelectionChange();

  destroy(): void {
    if (!!this.quill) {
      this.quill.root.removeEventListener("compositionstart", this.quillRootCompositionstartListener);
      this.quill.root.removeEventListener("compositionupdate", this.quillRootCompositionupdateListener);
      this.quill.root.removeEventListener("compositionend", this.quillRootCompositionendListener);
      this.quill.root.removeEventListener("keydown", this.quillRootKeydownListener);
      this.quill.root.removeEventListener("keyup", this.quillRootKeyupListener);
      this.quill.root.removeEventListener("focusout", this.quillRootFocusoutListener);
      this.quill = null;
      delete this.quill;
    }
    document.removeEventListener("selectionchange", this.onSelectionChangeListener);
    if (!!this.observer) {
      this.observer.disconnect();
      this.observer = null;
      delete this.observer;
    }
    clearTimeout(this.observerTimer);
    this.observerTimer = null;
    delete this.observerTimer;
  }

  getCompositing(): boolean {
    return this.compositing;
  }

  private quillRootCompositionstart(e: CompositionEvent): void {
    // this._logger.debug("compositionstart");
    // IME使用中にフォーカスが外れる場合があり、復元するまで処理しない
    if (this.compositionCanceling) {
      e.preventDefault();
      return;
    }
    this.compositing = true;
    this.compositionupdated = false;
    // IME使用中にフォーカスが外れる場合があるため復元用に保持する
    this.compositingContents = this.quill.getContents();
    this.compositingRange = this.quill.getSelection();
  }

  private quillRootCompositionupdate(e: CompositionEvent): void {
    // this._logger.debug("compositionupdate");
    // IME使用中にフォーカスが外れる場合があり、復元するまで処理しない
    if (this.compositionCanceling) {
      e.preventDefault();
      return;
    }
    this.compositionupdated = true;
  }

  private quillRootCompositionend(e: CompositionEvent): void {
    // this._logger.debug("compositionend");
    this.compositing = false;
    this.compositionupdated = true;
    // IME使用中にフォーカスが外れる場合があり、復元するまで処理しない
    if (this.compositionCanceling) {
      e.preventDefault();
      return;
    }
    // IMEで文字を入力し、行の先頭に戻り再度1文字入力する。そのまま改行すると何故か後ろの文字が次の行に折り返さない現象が発生した
    // 調査したところ、行の先頭に戻り再度入力確定したタイミングでQuillの選択状態が行末に移動していた
    // そのため、ブラウザの選択位置と比較し復旧を行う
    if (!!this.quill && this.quill.hasFocus()) {
      const range = this.quill.getSelection();
      const selection = this.getSelection();
      if (!!range && range.index !== selection.index) {
        // MutationCallbackのTimeout処理でもエラーが発生する場合があるので、過度な復旧処理を行わないようにする
        if (this.observerTimer !== null) {
          clearTimeout(this.observerTimer);
        }
        // 選択位置で不一致の場合、丸ごと入れ直すことによりQuillの復帰を試みる
        this._logger.warn("Quillでテキスト選択位置の不一致が発生しました。復帰を試みます");
        this.repairQuillData();
        return;
      }
    }
    // IME入力確定時、MutationCallbackのTimeout処理が先に動作してしまうことによりonTextChange()が呼ばれない場合がある。そのためのフォローアップを行う
    this.quillMutationCallback();
  }

  private quillRootKeydown(e: KeyboardEvent): void {
    // this._logger.debug("keydown", e.key);
    this.correctSelectionStatus(e);
    // IEでIMEを使用して入力中にEscを押すとオブジェクトのフォーカスが外れるがキャレットが残る場合があった。明示的に解除を入れる
    if (e.key === "Esc") {
      this.quill.blur();
    }
  }

  private quillRootKeyup(e: KeyboardEvent): void {
    // this._logger.debug("keyup", e.key);
    // IME使用中にフォーカスが外れる場合があり、復元するまで処理しない
    if (this.compositionCanceling) {
      return;
    }
    // IEでIMEを使用した入力が出来なくなる段落が存在したための対応
    // 通常compositionstartイベントが発行された場合、keyupまでにcompositionupdateかcompositionendが呼ばれるためそこで問題が起きたかを判定する
    // 改行→IMEで入力を素早く行うとkeyupがcompositionstartと同タイミングで発行される場合があったため遅延して処理する
    setTimeout(() => {
      if (this.compositionupdated === false) {
        this._logger.warn("QuillでIMEを使用したテキスト入力に問題が発生しました。復帰を試みます");
        this.quillRefresh();
      }
      this.compositionupdated = null;
    });
  }

  private quillRootFocusout(e: FocusEvent): void {
    // IMEを使用して改行を削除した直後に入力を行い確定せずバックスペースでキャンセルするとフォーカスが外れる現象が発生した
    // 発生時はrelatedTargetがbodyであったため、これを条件に判定する
    // IMEを使用している場合のみに問題になるが、compositionendイベント後に発生するため復元用の情報がある場合を使用した
    // また、連続して復帰しないようにするため復元用の情報は削除する
    const contents = this.compositingContents;
    const range = this.compositingRange;
    this.compositingContents = null;
    this.compositingRange = null;
    if (!this.quill || !e.relatedTarget || !(e.relatedTarget instanceof HTMLBodyElement) || !contents || !range) {
      return;
    }
    // ATOKを使用して入力済みの行の先頭から入力を行い確定させた際、行全体が消える現象が発生した
    // F12ツールを起動した状態では発生せず。また、確定タイミングや文字の長さによって発生しないこともあった。Quillの処理タイミングによるものと思われる
    // Quillは文字削除のイベントを発行していたが、直前にフォーカスが外れていたために誤判定していたと思われる
    // また、行頭から2文字以上選択した状態からIMEを使用して入力しても同様の現象が発生した
    // IME使用中にフォーカスが外れた場合、フォーカスおよび直前の内容を復元させる
    this._logger.warn("QuillでIMEを使用したテキスト入力中にフォーカスが外れました。復帰を試みます");
    this.quill.focus();
    this.compositionCanceling = true;
    // Google日本語入力では正しく復元できなかっため遅延で呼び出し
    setTimeout(() => {
      if (!this.compositionCanceling) {
        return;
      }
      this.quill.setContents(contents);
      this.quill.setSelection(range);
      this.compositionCanceling = false;
    });
  }

  private onSelectionChange(): void {
    // this._logger.debug("onSelectionChange");
    if (this.compositing) { return; }
    this.correctSelectionStatus(null);
    // IEで範囲選択が行えない場合があるため選択状態を監視する
    // 問題が出るパターンは以下の場合
    // ・入力済みの段落の先頭に追加で入力後、先頭に向かって範囲選択する場合

    // 連続した改行のみの行がある状態で、以下の動作でIEが落ちる現象が発生
    // 1. 行をクリック→キャレットが見える
    // 2. 同じ行をダブルクリック→キャレットが消える（逆方向に1文字選択されている）
    // 3 .一つ前の行をダブルクリック→落ちる
    // quill.getSelection()内の処理で落ちており、setTimeout()を挟んだところ発生しなくなった
    setTimeout(() => {
      if (!this.quill || !this.quill.hasFocus()) {
        return;
      }
      const range = this.quill.getSelection();
      if (!range) {
        return;
      }
      const selection = window.getSelection();
      if (this.quill.getText(range.index, range.length).replace(/\s/g, "") === selection.toString().replace(/\s/g, "")) {
        return;
      }
      // 選択状態の文字列で不一致の場合、丸ごと入れ直すことによりQuillの復帰を試みる
      this._logger.warn("Quillでテキスト選択状態の不一致が発生しました。復帰を試みます");
      this.repairQuillData();
    });
  }

  private correctSelectionStatus(e?: KeyboardEvent): void {
    // 操作により選択位置がQuillの意図しない場所に移動することがあるため補正する
    if (!this.quill || !this.quill.hasFocus()) {
      return;
    }
    const quillRange = this.quill.getSelection();
    const selection = window.getSelection();
    if (selection.rangeCount === 0) {
      return;
    }
    const cursor = this.quill.root.querySelector("span.ql-cursor");
    const range = selection.getRangeAt(0);
    const clone = range.cloneRange();

    // 始点がQuillのキャレットを表す文字コード(0xFEFF)の左側を選択された場合、右に移動する
    if (range.startContainer.nodeType === Node.TEXT_NODE && range.startContainer.textContent === String.fromCharCode(0xFEFF) && range.startOffset === 0) {
      clone.setStart(range.startContainer, 1);
    }

    // Quillのキャレットを跨ぐ選択(逆方向に選択)が行われた場合
    if (!!cursor && cursor.compareDocumentPosition(range.startContainer) & document.DOCUMENT_POSITION_PRECEDING && cursor.compareDocumentPosition(range.endContainer) & document.DOCUMENT_POSITION_FOLLOWING) {
      // 文字入力が行われた場合
      if (!!e && !e.ctrlKey && e.key.length === 1) {
        // 選択状態の補正では補正しきれないためQuill経由で現在の選択範囲の削除を行う
        e.preventDefault();
        this.quill.deleteText(quillRange.index, quillRange.length);
        return;
      }
    }

    // 末端のNodeを取る関数
    const getFirstChildAtEnd: (n: Node) => Node = (n) => {
      return !!n.firstChild
        ? getFirstChildAtEnd(n.firstChild)
        : n;
    };
    const getLastChildAtEnd: (n: Node) => Node = (n) => {
      return !!n.lastChild
        ? getLastChildAtEnd(n.lastChild)
        : n;
    };

    // 始点がDIV or Pを選択してしまう場合
    if (range.startContainer.nodeType === Node.ELEMENT_NODE) {
      if (range.startContainer.nodeName === "DIV") {
        // 最終段落以外
        const startChildNode = range.startContainer.childNodes[range.startOffset];
        if (!!startChildNode) {
          // 配下にQuillのキャレットが存在する場合
          if (!!cursor && startChildNode.contains(cursor)) {
            // Quillのキャレットに移動
            clone.setStart(cursor, 1);
          } else {
            // 末端の先頭に移動
            clone.setStartBefore(getFirstChildAtEnd(startChildNode));
          }
        }
      } else if (range.startContainer.nodeName === "P") {
        // 最終以外
        const startChildNode = range.startContainer.childNodes[range.startOffset];
        if (!!startChildNode) {
          // 配下にQuillのキャレットが存在する場合
          if (!!cursor && startChildNode.contains(cursor)) {
            // Quillのキャレットに移動
            clone.setStart(cursor, 1);
          }
        }
      }
    }

    // 終点がDIV選択してしまう場合
    if (range.endContainer.nodeType === Node.ELEMENT_NODE) {
      if (range.endContainer.nodeName === "DIV") {
        // 最終段落以外
        const endChildNode = range.endContainer.childNodes[range.endOffset];
        if (!!endChildNode) {
          // 末端の先頭に移動
          clone.setEndBefore(getFirstChildAtEnd(endChildNode));
        } else {
          // 最終段落の末端がテキストの場合
          const endNode = getLastChildAtEnd(range.endContainer.childNodes[range.endOffset - 1]);
          if (endNode.nodeType === Node.TEXT_NODE && endNode.textContent !== String.fromCharCode(0xFEFF)) {
            // 末端の最終に移動
            clone.setEndAfter(endNode);
          } else {
            // 末端の先頭に移動
            clone.setEndBefore(endNode);
          }
        }
      }
    }

    // 補正されない場合、終了
    if (clone.compareBoundaryPoints(Range.START_TO_START, range) === 0 && clone.compareBoundaryPoints(Range.END_TO_END, range) === 0) {
      return;
    }

    // 補正された場合、選択し直す
    document.removeEventListener("selectionchange", this.onSelectionChangeListener);
    selection.removeAllRanges();
    document.addEventListener("selectionchange", this.onSelectionChangeListener);
    selection.addRange(clone);
  }
  
  private quillMutationObserver(): void {
    // IEでQuillの情報と表示中のDOMが不一致起こす場合があるためDOMを監視する
    // 問題が出るパターンは以下の場合
    // ・入力済みの段落の先頭に追加で入力する場合
    this.observer = new MutationObserver(() => this.quillMutationCallback());
    this.observer.observe(this.quill.root, RichTextBoxIeQuillBehaviorCorrector.mutationObserverInit);
  }

  private quillMutationCallback(): void {
    // this._logger.debug("MutationCallback A");
    // 過剰なチェックを行わないようにする
    if (this.observerTimer !== null) {
      clearTimeout(this.observerTimer);
    }
    // Quillのキャレットを跨ぐ選択(逆方向に選択)が行われた場合、DOM変更が行われ続けることがあった
    // 連続して行われないようQuillの変更が無ければ処理しない
    const contents = JSON.stringify(this.quill.getContents());
    const lastContents = JSON.stringify(this.lastContents);
    if (!!this.lastContents && contents === lastContents) {
      this.lastContents = null;
      return;
    }
    // IME使用中にフォーカスが外れる場合があり、復元するまで処理しない
    if (this.compositionCanceling) {
      return;
    }
    this.observerTimer = setTimeout(() => {
      // this._logger.debug("MutationCallback B");
      if (!this.quill) {
        return;
      }
      // IME使用中はチェックしない
      if (this.compositing) {
        // IME使用中にフォーカスが外れる場合があるため復元用に保持する
        this.compositingContents = this.quill.getContents();
        this.compositingRange = this.quill.getSelection();
        return;
      }
      if (this.isValidHtml()) {
        this.lastContents = this.quill.getContents();
        this.onTextChange();
        return;
      }
      // DOMの変更時不一致が起きていた場合、丸ごと入れ直すことによりQuillの復帰を試みる
      this._logger.warn("Quillでテキストの不一致が発生しました。復帰を試みます");
      this.repairQuillData();
    });

    // DOMの変更スタックのチェックは次のタイミングで随時行う
    setTimeout(() => {
      // this._logger.debug("MutationCallback C");
      // このタイミングでDOMの変更スタックが100を超えていた場合は異常とする。
      // https://github.com/quilljs/parchment/blob/067c392fe1e6f9b2c480bf4cc00a2e10a72e5ef1/src/blot/scroll.ts#L98
      // 100の根拠は上記URLのMAX_OPTIMIZE_ITERATIONSの値より
      if (!!this.observer && this.observer.takeRecords().length >= 100) {
        // 通常の復帰が見込めないため(すでに表示中のHTMLが異常なため)エラーを返す
        this._logger.error("Quill error(observer takeRecords)");
        this.onError();
      }
    });
  }

  private isValidHtml(): boolean {
    try {
      // 文字の長さが異なっていないか確認
      const quillText = this.quill.getText().replace(/\s/g, "");
      const innerText = this.quill.root.innerText.replace(/\s/g, "");
      if (quillText !== innerText) {
        return false;
      }
      // テキストが不正に生成されていないか確認
      const root = this.quill.root;
      const rootLength = root.childNodes.length;
      for (let i = 0; i < rootLength; i++) {
        if (root.childNodes[i].nodeType === Node.TEXT_NODE) {
          // root直下にテキストが生成されていた場合
          return false;
        }
      }
    } catch (e) {
      this._logger.error("Quill error(isValidHtml)", e.message);
      return false;
    }
    return true;
  }

  private repairQuillData(): void {
    this.lastContents = null;
    if (!this.quill) {
      return;
    }
    const selectionParam = this.getSelection();
    const element = this.toRepairRichTextAsElement();
    const deleteLength = this.quill.root.textContent.length - element.textContent.length;
    const setIndex = Math.max(0, selectionParam.index - deleteLength);
    setTimeout(() => {
      if (!this.quill) {
        return;
      }
      try {
        this.quill.setContents(this.quill.clipboard.convert(element.innerHTML as any), "silent"); // convertの引数は型定義と異なるため変換
        this.quill.setSelection(setIndex, selectionParam.length, "silent"); // 選択状態を復元
      } catch (e) {
        this._logger.error("Quill error(repairQuillData)", e.message);
        this.onError();
      }
    });
  }

  private getSelection(): RangeStatic {
    const windowSelection = window.getSelection();
    if (windowSelection.rangeCount === 0) {
      return { index: 0, length: 0 };
    }
    const range = windowSelection.getRangeAt(0);
    if (this.isInvalidNode(range.startContainer)) {
      const selection = this.quill.getSelection();
      return !!selection
        ? selection
        : { index: 0, length: 0 };
    }
    const startIndex = range.startContainer.nodeType === Node.TEXT_NODE
      ? this.getNodeIndex(this.getPrevNode(range.startContainer), range.startOffset)
      : this.getNodeIndex(this.getPrevNode(range.startContainer.childNodes.length > 0 ? range.startContainer.childNodes[0] : range.startContainer), this.getNodeLength(range.startContainer));
    const endIndex = range.endContainer.nodeType === Node.TEXT_NODE
      ? this.getNodeIndex(this.getPrevNode(range.endContainer), range.endOffset)
      : this.getNodeIndex(this.getPrevNode(range.endContainer.childNodes.length > 0 ? range.endContainer.childNodes[0] : range.endContainer), this.getNodeLength(range.endContainer));
    return {
      index: startIndex,
      length: endIndex - startIndex,
    };
  }
 
  private isInvalidNode(node: Node): boolean {
    if (node.nodeType === Node.TEXT_NODE && node.parentNode !== null && node.parentNode.nodeType === Node.ELEMENT_NODE) {
      // 装飾付きの文字列を改行した際に以下のタグが選択されることがあるが、文書としての意味はなく選択範囲がずれる要因となるため、とりあえずQuillが正として扱う
      // <span class="ql-cursor">﻿</span>
      const element = node.parentNode as HTMLElement;
      return element.className === "ql-cursor";
    }
    return false;
  }
 
  private getPrevNode(node: Node): { node: Node, pos: "root" | "prev" | "parent" } {
    if (!node) {
      return { node: null, pos: "root" };
    } else if (node.previousSibling !== null) {
      return { node: node.previousSibling, pos: "prev" };
    } else if (node.parentNode !== null && node.parentNode.nodeName !== "DIV") {
      return { node: node.parentNode, pos: "parent" };
    } else {
      return { node: node.parentNode, pos: "root" };
    }
  }
 
  private getNodeIndex(current: { node: Node, pos: string }, index: number): number {
    if (current.pos === "prev") {
      return this.getNodeIndex(this.getPrevNode(current.node), index + this.getNodeLength(current.node));
    } else if (current.pos === "parent") {
      return this.getNodeIndex(this.getPrevNode(current.node), index);
    } else {
      return index;
    }
  }
 
  private getNodeLength(node: Node): number {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      const text = element.innerText !== "\n" ? element.innerText.length : 0; // innerTextは改行を含んだ文字列、改行しかない場合は0
      const block = element.tagName.toUpperCase() === "P" ? 1 : 0;  // ブロック要素の場合は改行として+1
      return text + block;
    } else {
      return node.textContent !== null ? node.textContent.length : 0;
    }
  }

  private toRepairRichTextAsElement(): HTMLDivElement {
    const clone = this.quill.root.cloneNode(true) as HTMLDivElement;
    // テキストが不正に生成されている場合があるため削除する
    for (let i = 0; i < clone.childNodes.length;) {
      if (clone.childNodes[i].nodeType === Node.TEXT_NODE) {
        // root直下にテキストが生成されていた場合
        clone.removeChild(clone.childNodes[i]);
        continue;
      } else if (clone.childNodes[i].nodeName === "P") {
        const p = clone.childNodes[i];
        for (let j = 0; j < p.childNodes.length;) {
          if (p.childNodes[j].nodeType === Node.TEXT_NODE) {
            // pタグ直下にテキストが生成されていた場合
            p.removeChild(p.childNodes[j]);
            continue;
          }
          j++;
        }
        if (!p.hasChildNodes()) {
          // テキストが無ければ改行を追加
          p.appendChild(document.createElement("br"));
        }
      }
      i++;
    }
    // Quillの選択位置を示すDOMは取り除く
    const cursor = clone.getElementsByClassName("ql-cursor");
    if (cursor.length > 0) {
      const parent = cursor[0].parentNode;
      parent.removeChild(cursor[0]);
      if (!parent.hasChildNodes() && parent.parentNode && parent.parentNode.nodeName === "P") {
        // テキストが無ければ改行に置き換え
        parent.parentNode.appendChild(document.createElement("br"));
        parent.parentNode.removeChild(parent);
      }
    }
    return clone;
  }

  private quillRefresh(range?: RangeStatic): void {
    // IEでIMEを使用した入力が出来なくなる段落が存在したための対応
    // 半角文字を入力することにより復帰することから強制的に文字を書き込み削除する
    if (!this.quill) {
      return;
    }
    const _range = !!range ? range : this.quill.getSelection();
    const index = !!_range ? _range.index : 0;
    setTimeout(() => {
      if (!this.quill) {
        return;
      }
      this.quill.insertText(index, String.fromCharCode(0xFEFF), "silent");  // スペースの場合ちらつきが目立つため、Quillの選択位置に含まれている文字コードを使用した
      this.quill.deleteText(index, 1, "silent");
    });
  }
}
